import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Check, FileText, Loader2, Upload, X } from "lucide-react";
import { useId, useRef, useState } from "react";
import { MetaText } from "@/components/plan-audit/AuditPrimitives";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  acceptFor,
  type TrackedUpload,
  uploadKey,
  useUploadAuditFiles,
} from "@/hooks/use-audit-upload";
import { useGuide, useSolution, useSolutions, useStartAudit } from "@/hooks/use-plan-audit";
import { ENGAGEMENT_STAGE_OPTIONS, type EngagementStage } from "@/lib/enums";
import { formatBytes } from "@/lib/plan-audit-format";

/** The `<select>` value that means "create one" rather than naming an existing. */
const NEW_SOLUTION = "__new__";

/**
 * New audit — uploading and filing are one action.
 *
 * The design's central claim about this screen is that there is no separate
 * "process" step, and it is also where a solution is BORN: choosing (or naming)
 * the solution, its plan, its intake, and pressing go is the whole flow. There
 * is no "create a solution" screen anywhere, deliberately — a solution with no
 * plan against it has nothing to show.
 *
 * **Filing is real; running is not.** The upload lands in storage, the solution
 * and plan version are created, and an audit run is queued — but nothing scores
 * it, because the audit engine is a separate piece of work. The button says
 * "queue", the run screen says nothing is processing it, and the side panel
 * below says so before you press anything. The alternative — a button labelled
 * "run" that quietly produces a run nobody will ever look at — is the thing this
 * screen has always refused to do.
 *
 * The Claude-connection gate that used to sit in front of this is gone for now:
 * queueing bills nobody's subscription. It belongs back here when the engine
 * lands, which is the point it starts costing something.
 */
export function NewAuditView({ preselectedSolution }: { preselectedSolution?: string }) {
  const navigate = useNavigate();
  const planInputId = useId();
  const intakeInputId = useId();
  const planInputRef = useRef<HTMLInputElement>(null);
  const intakeInputRef = useRef<HTMLInputElement>(null);

  const [solutionSlug, setSolutionSlug] = useState(preselectedSolution ?? "");
  const [newSolution, setNewSolution] = useState({
    name: "",
    sector: "",
    stage: "pre-sales" as EngagementStage,
  });
  const [plan, setPlan] = useState<File | null>(null);
  const [intake, setIntake] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const solutions = useSolutions(
    { search: "", status: "", sort: "name", sortDir: "asc" },
    { cursor: null, direction: "next" },
  );
  const { uploads, upload, reset: resetUploads } = useUploadAuditFiles();
  const startAudit = useStartAudit();
  const guide = useGuide();

  const creatingSolution = solutionSlug === NEW_SOLUTION;
  const chosen = (solutions.data?.data ?? []).find((s) => s.slug === solutionSlug);
  // The version this upload will become. `useSolution` carries every version, so
  // max + 1 — not length, which would mislabel a solution with a gap.
  const existing = useSolution(creatingSolution ? "" : solutionSlug);
  const nextVersion = creatingSolution
    ? 1
    : Math.max(0, ...(existing.data?.versions ?? []).map((v) => v.versionNumber)) + 1;
  const targetStage = creatingSolution ? newSolution.stage : (chosen?.stage ?? "pre-sales");

  // A run must name the standard it scored against, and `guide_version_id` is
  // NOT NULL — so with nothing published there is no audit to file. Checked
  // BEFORE uploading: burning 20 MB on a request that will be refused is the
  // avoidable version of this failure.
  const guideUnpublished = guide.data !== undefined && guide.data.publishedAt === null;

  const ready =
    (creatingSolution
      ? newSolution.name.trim().length >= 2 && newSolution.sector.trim().length >= 2
      : solutionSlug.length > 0) &&
    plan !== null &&
    intake.length > 0 &&
    !guideUnpublished;

  function validate(): string {
    if (solutionSlug.length === 0) return "Choose which solution this plan belongs to.";
    if (creatingSolution && newSolution.name.trim().length < 2) return "Give the solution a name.";
    if (creatingSolution && newSolution.sector.trim().length < 2) {
      return "Say which sector the client is in.";
    }
    if (plan === null) return "The Solution Plan file is missing.";
    if (intake.length === 0) return "At least one intake file is required.";
    return "";
  }

  async function handleStart() {
    const problem = validate();
    if (problem.length > 0) {
      setError(problem);
      return;
    }
    if (plan === null) return;
    setError("");
    setBusy(true);
    try {
      const items = [
        {
          key: uploadKey(plan, "plan", 0),
          file: plan,
          role: "plan" as const,
          state: "waiting" as const,
        },
        ...intake.map((file, index) => ({
          key: uploadKey(file, "intake", index),
          file,
          role: "intake" as const,
          state: "waiting" as const,
        })),
      ];
      const outcome = await upload(items);

      // An incomplete intake does NOT become a run. A plan scored against two of
      // three intake files manufactures "the intake said X and the plan ignored
      // it" flags against content that was uploaded and then dropped — the flags
      // screen would be confidently wrong, and nothing would say so.
      if (outcome.failed.length > 0) {
        setError(
          outcome.failed.length === items.length
            ? "None of the files uploaded. Nothing has been filed."
            : "Some files did not upload, so nothing has been filed. Retry them below.",
        );
        return;
      }

      const planUpload = outcome.uploaded.find((row) => row.role === "plan");
      const intakeUploads = outcome.uploaded.filter((row) => row.role === "intake");
      if (planUpload?.fileId === undefined) {
        setError("The plan file did not upload. Nothing has been filed.");
        return;
      }

      const filenames: Record<string, string> = {};
      for (const row of outcome.uploaded) {
        if (row.fileId !== undefined) filenames[row.fileId] = row.file.name;
      }

      const result = await startAudit.mutateAsync({
        solution: creatingSolution
          ? {
              kind: "new",
              name: newSolution.name.trim(),
              sector: newSolution.sector.trim(),
              stage: newSolution.stage,
            }
          : { kind: "existing", slug: solutionSlug },
        planFileId: planUpload.fileId,
        intakeFileIds: intakeUploads.flatMap((row) => (row.fileId ? [row.fileId] : [])),
        filenames,
      });
      await navigate({ to: "/runs/$reference", params: { reference: result.reference } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not queue the audit.");
    } finally {
      setBusy(false);
    }
  }

  if (solutions.isError) {
    return (
      <ErrorState message={solutions.error.message} onRetry={() => void solutions.refetch()} />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px] lg:items-start">
      <div className="pa-card">
        <div className="pa-card-head">
          <h2>Upload and queue</h2>
          <MetaText>One step, not two</MetaText>
        </div>

        {guideUnpublished && (
          <div
            className="mb-4 rounded-lg border p-3"
            style={{ background: "var(--warning-bg)", borderColor: "var(--warning)" }}
          >
            <p className="text-[13px] font-semibold" style={{ color: "var(--warning-fg)" }}>
              The Plan Guide has no published version
            </p>
            <p className="mt-1 text-xs" style={{ color: "var(--warning-fg)" }}>
              A score is only meaningful against a published standard, so an audit cannot be filed
              until one exists.{" "}
              <Link to="/guide" className="font-semibold underline">
                Open the Plan Guide
              </Link>
              .
            </p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="pa-eyebrow mb-1.5 block" htmlFor={`${planInputId}-solution`}>
              Solution
            </label>
            {solutions.isLoading ? (
              <Skeleton className="h-10 w-full rounded-lg" />
            ) : (
              <select
                id={`${planInputId}-solution`}
                value={solutionSlug}
                onChange={(e) => {
                  setSolutionSlug(e.target.value);
                  setError("");
                }}
                className="w-full rounded-lg border border-[var(--border-2)] bg-white px-3 py-2 text-sm text-[var(--fg-1)]"
              >
                <option value="">Choose a solution…</option>
                <option value={NEW_SOLUTION}>＋ New solution…</option>
                {(solutions.data?.data ?? []).map((s) => (
                  <option key={s.id} value={s.slug}>
                    {s.name} — {s.sector}
                  </option>
                ))}
              </select>
            )}
            <MetaText>
              {creatingSolution
                ? "new solution"
                : chosen
                  ? `existing · ${chosen.versionCount} version${chosen.versionCount === 1 ? "" : "s"}`
                  : "The plan is filed as a version of this solution, so its score can be compared with earlier ones."}
            </MetaText>
          </div>

          <div>
            <span className="pa-eyebrow mb-1.5 block">This upload becomes</span>
            <div className="flex items-center gap-2 rounded-lg border border-[var(--border-2)] bg-white px-3 py-2">
              <span className="flex-1 truncate text-sm font-semibold text-[var(--brand-primary)]">
                {solutionSlug.length === 0 ? "—" : `Plan v${nextVersion} · ${targetStage}`}
              </span>
              {solutionSlug.length > 0 && (
                <MetaText>{nextVersion === 1 ? "first version" : "new version"}</MetaText>
              )}
            </div>
          </div>
        </div>

        {creatingSolution && (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div>
              <label className="pa-eyebrow mb-1.5 block" htmlFor={`${planInputId}-name`}>
                Name
              </label>
              <Input
                id={`${planInputId}-name`}
                value={newSolution.name}
                placeholder="Kestrel Field Service"
                onChange={(e) => {
                  setNewSolution((prev) => ({ ...prev, name: e.target.value }));
                  setError("");
                }}
              />
            </div>
            <div>
              <label className="pa-eyebrow mb-1.5 block" htmlFor={`${planInputId}-sector`}>
                Sector
              </label>
              <Input
                id={`${planInputId}-sector`}
                value={newSolution.sector}
                placeholder="Utilities"
                onChange={(e) => {
                  setNewSolution((prev) => ({ ...prev, sector: e.target.value }));
                  setError("");
                }}
              />
            </div>
            <div>
              <span className="pa-eyebrow mb-1.5 block">Engagement</span>
              <EnumSelect
                value={newSolution.stage}
                onValueChange={(value) =>
                  setNewSolution((prev) => ({ ...prev, stage: value as EngagementStage }))
                }
                options={ENGAGEMENT_STAGE_OPTIONS}
                aria-label="Engagement stage"
              />
            </div>
          </div>
        )}

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className="pa-eyebrow mb-1.5 block" htmlFor={planInputId}>
              Solution Plan · 1 file
            </label>
            <input
              ref={planInputRef}
              id={planInputId}
              type="file"
              accept={acceptFor("plan")}
              className="sr-only"
              onChange={(e) => {
                setPlan(e.target.files?.[0] ?? null);
                setError("");
              }}
            />
            <DropZone
              filled={plan !== null}
              onClick={() => planInputRef.current?.click()}
              label={plan === null ? "Choose the plan file" : plan.name}
              hint={plan === null ? "Markdown, text, PDF or Word" : formatBytes(plan.size)}
              onClear={plan === null ? undefined : () => setPlan(null)}
            />
          </div>

          <div>
            <label className="pa-eyebrow mb-1.5 block" htmlFor={intakeInputId}>
              Intake · {intake.length === 0 ? "1 or more" : `${intake.length} file`}
              {intake.length > 1 ? "s" : ""}
            </label>
            <input
              ref={intakeInputRef}
              id={intakeInputId}
              type="file"
              multiple
              accept={acceptFor("intake")}
              className="sr-only"
              onChange={(e) => {
                const chosenFiles = Array.from(e.target.files ?? []);
                // Appended, not replaced: the intake usually arrives as several
                // files picked in separate goes, and replacing silently loses the
                // earlier ones.
                setIntake((prev) => [...prev, ...chosenFiles]);
                setError("");
              }}
            />
            <DropZone
              filled={intake.length > 0}
              onClick={() => intakeInputRef.current?.click()}
              label={intake.length === 0 ? "Add intake files" : "Add more"}
              hint="Transcripts, notes — as many as the intake took"
            />
            {intake.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {intake.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}`}
                    className="flex items-center gap-2 rounded-md bg-[var(--bg-2)] px-2 py-1.5"
                  >
                    <FileText
                      aria-hidden="true"
                      size={14}
                      className="shrink-0 text-[var(--fg-3)]"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-[var(--fg-1)]">
                      {file.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-[var(--fg-3)]">
                      {formatBytes(file.size)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIntake((prev) => prev.filter((_, i) => i !== index))}
                      className="shrink-0 rounded p-0.5 text-[var(--fg-3)] hover:text-[var(--danger)]"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X aria-hidden="true" size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {uploads.length > 0 && <UploadList uploads={uploads} />}

        {error.length > 0 && (
          <p
            className="mt-4 rounded-lg border px-3 py-2 text-[13px]"
            style={{
              background: "var(--danger-bg)",
              borderColor: "var(--danger)",
              color: "var(--danger-fg)",
            }}
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => {
              resetUploads();
              void handleStart();
            }}
            disabled={!ready || busy}
          >
            <Upload aria-hidden="true" />
            {busy ? "Uploading…" : "Upload & queue audit"}
          </Button>
          <MetaText>
            The audit is filed and queued. Running it is not part of this build yet.
          </MetaText>
        </div>
      </div>

      <SidePanel />
    </div>
  );
}

/** Per-file upload state. No percentage — see the note in use-audit-upload.ts. */
function UploadList({ uploads }: { uploads: TrackedUpload[] }) {
  return (
    <ul className="mt-4 flex flex-col gap-1">
      {uploads.map((row) => (
        <li
          key={row.key}
          className="flex items-center gap-2 rounded-md px-2 py-1.5"
          style={{
            background: row.state === "failed" ? "var(--danger-bg)" : "var(--bg-2)",
          }}
        >
          {row.state === "uploaded" ? (
            <Check aria-hidden="true" size={14} style={{ color: "var(--success)" }} />
          ) : row.state === "failed" ? (
            <AlertCircle aria-hidden="true" size={14} style={{ color: "var(--danger)" }} />
          ) : (
            <Loader2
              aria-hidden="true"
              size={14}
              className={row.state === "uploading" ? "animate-spin" : ""}
              style={{ color: "var(--fg-3)" }}
            />
          )}
          <span className="min-w-0 flex-1 truncate text-xs text-[var(--fg-1)]">
            {row.file.name}
          </span>
          <span
            className="shrink-0 text-[11px]"
            style={{ color: row.state === "failed" ? "var(--danger-fg)" : "var(--fg-3)" }}
          >
            {row.state === "failed" ? (row.error ?? "failed") : UPLOAD_STATE_LABELS[row.state]}
          </span>
        </li>
      ))}
    </ul>
  );
}

const UPLOAD_STATE_LABELS: Record<TrackedUpload["state"], string> = {
  waiting: "waiting",
  uploading: "uploading…",
  uploaded: "uploaded",
  failed: "failed",
};

function DropZone({
  filled,
  onClick,
  label,
  hint,
  onClear,
}: {
  filled: boolean;
  onClick: () => void;
  label: string;
  hint: string;
  onClear?: () => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full flex-col items-center gap-1 rounded-xl border border-dashed px-4 py-6 text-center transition-colors"
        style={{
          borderColor: filled ? "var(--brand-accent)" : "var(--border-2)",
          background: filled ? "var(--bg-accent)" : "var(--bg-2)",
        }}
      >
        <Upload
          aria-hidden="true"
          size={18}
          style={{ color: filled ? "var(--brand-accent)" : "var(--fg-3)" }}
        />
        <span className="max-w-full truncate text-[13px] font-semibold text-[var(--fg-1)]">
          {label}
        </span>
        <span className="text-[11px] text-[var(--fg-3)]">{hint}</span>
      </button>
      {onClear !== undefined && (
        <button
          type="button"
          onClick={onClear}
          className="absolute top-2 right-2 rounded p-1 text-[var(--fg-3)] hover:text-[var(--danger)]"
          aria-label="Remove file"
        >
          <X aria-hidden="true" size={14} />
        </button>
      )}
    </div>
  );
}

function SidePanel() {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h3>How a run works</h3>
      </div>
      <ol className="flex flex-col gap-3">
        {[
          [
            "Assemble inputs",
            "The plan, every intake file and the current Plan Guide are loaded as one input set.",
          ],
          [
            "Score every section",
            "Each section is compared to the stored standard, and a commentary and proposed value are written per section.",
          ],
          [
            "Detect hallucinations and gaps",
            "The plan is cross-checked against all intake files for claims it cannot support and content it dropped.",
          ],
          [
            "Record the run",
            "Scores, flags and the Guide version are written against this plan version, so the result stays reproducible.",
          ],
        ].map(([title, detail], index) => (
          <li key={title} className="flex gap-3">
            <span
              className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold"
              style={{ background: "var(--bg-3)", color: "var(--fg-2)" }}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <div>
              <div className="text-[13px] font-semibold text-[var(--fg-1)]">{title}</div>
              <p className="mt-0.5 text-xs leading-relaxed text-[var(--fg-2)]">{detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-4 border-t border-[var(--border-1)] pt-3 text-xs leading-relaxed text-[var(--fg-2)]">
        Steps 2 to 4 are not part of this build. Your upload is stored and the audit is queued
        against the published Guide; nothing scores it yet.
      </p>
    </div>
  );
}
